import React, { useState, useRef, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Container,
  Grid,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  TextField,
  InputAdornment,
  Breadcrumbs,
  Link,
  Chip,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import {
  Search,
  Download,
  CalendarToday,
  ArrowBack,
  TrendingUp,
  AccountBalanceWallet,
  PeopleAlt,
  SwapHoriz,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import adminService from "../../redux/admin/adminService";
import { colors } from "../../constants/colors";

interface AssetSummaryRow {
  assetType: number;
  assetClassName: string;
  totalInvested: number;
  totalCurrent: number;
  totalGainLoss: number;
  transactionCount: number;
  uniqueInvestors: number;
}

interface TransactionRow {
  name: string;
  aumUserId: string;
  accountType: string;
  assetClassName: string;
  investedAmount: number;
  currentAmount: number;
  gainLoss: number;
  transactionDate: string;
}

interface RealisedAssetSummaryRow {
  assetType: number;
  assetClassName: string;
  totalAmount: number;
  transactionCount: number;
  uniqueInvestors: number;
}

interface RealisedTransactionRow {
  name: string;
  aumUserId: string;
  accountType: string;
  assetClassName: string;
  sellAmount: number;
  sellCount: number;
  transactionDate: string;
}

interface RealisedAUTData {
  grandTotal: number;
  totalTransactions: number;
  totalInvestors: number;
  assetSummary: RealisedAssetSummaryRow[];
  transactions: RealisedTransactionRow[];
}

interface AUTData {
  grandTotal: number;
  grandTotalCurrent: number;
  grandTotalGainLoss: number;
  totalTransactions: number;
  totalInvestors: number;
  assetSummary: AssetSummaryRow[];
  transactions: TransactionRow[];
  realised?: RealisedAUTData;
  startDate: number;
  endDate: number;
}

const EMPTY_REALISED: RealisedAUTData = {
  grandTotal: 0,
  totalTransactions: 0,
  totalInvestors: 0,
  assetSummary: [],
  transactions: [],
};

const ASSET_CLASS_COLORS: Record<string, string> = {
  "Mutual Funds":         "#255288",
  "Fixed Deposits":       "#F17A21",
  "Stocks":               "#08991F",
  "Bonds":                "#7C3AED",
  "AIF":                  "#0891B2",
  "PMS":                  "#DB2777",
  "PPF":                  "#D97706",
  "Private Stock":        "#059669",
  "SIF":                  "#DC2626",
  "International Equity": "#2563EB",
  "Insurance":            "#9333EA",
  "Other":                "#6B7280",
};

const DATE_PRESETS = [
  { value: "1day",  label: "1D" },
  { value: "7day",  label: "7D" },
  { value: "1month",label: "1M" },
  { value: "1year", label: "1Y" },
  { value: "max",   label: "Max" },
  { value: "custom",label: "Custom" },
];

const formatAmount = (val: number): string => {
  if (!val && val !== 0) return "₹0";
  if (val >= 10000000) return `₹${(val / 10000000).toFixed(2)} Cr`;
  if (val >= 100000)   return `₹${(val / 100000).toFixed(2)} L`;
  if (val >= 1000)     return `₹${(val / 1000).toFixed(2)} K`;
  return `₹${val.toFixed(2)}`;
};

const formatGainLoss = (val: number): { text: string; color: string } => {
  const color = val > 0 ? "#08991F" : val < 0 ? "#DC2626" : colors.primary;
  const prefix = val > 0 ? "+" : "";
  return { text: `${prefix}${formatAmount(val)}`, color };
};

const getDateRange = (type: string): { startDate: number; endDate: number } => {
  const now = new Date();
  const endDate = now.getTime();
  let startDate: number;
  switch (type) {
    case "1day":   startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime(); break;
    case "7day":   startDate = endDate - 7  * 24 * 60 * 60 * 1000; break;
    case "1month": startDate = endDate - 30 * 24 * 60 * 60 * 1000; break;
    case "1year":  startDate = endDate - 365 * 24 * 60 * 60 * 1000; break;
    case "max":    startDate = new Date(2000, 0, 1).getTime(); break;
    default:       startDate = endDate - 7 * 24 * 60 * 60 * 1000;
  }
  return { startDate, endDate };
};

const DailyAUT: React.FC = () => {
  const navigate = useNavigate();
  const [dateType, setDateType] = useState<string>("7day");
  const [customStartDate, setCustomStartDate] = useState<string>("");
  const [customEndDate, setCustomEndDate] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<AUTData | null>(null);
  const [viewMode, setViewMode] = useState<"unrealised" | "realised">("unrealised");
  const [search, setSearch] = useState<string>("");
  const [realisedSearch, setRealisedSearch] = useState<string>("");
  const [selectedAsset, setSelectedAsset] = useState<string | null>(null);
  const [selectedRealisedAsset, setSelectedRealisedAsset] = useState<string | null>(null);
  const cacheRef = useRef<Record<string, AUTData>>({});

  const fetchData = useCallback(async (isCustomSubmit = false) => {
    let startDate: number;
    let endDate: number;

    if (dateType === "custom") {
      if (!isCustomSubmit) return;
      if (!customStartDate || !customEndDate) {
        toast.error("Please select both start and end dates.");
        return;
      }
      startDate = new Date(customStartDate).setHours(0, 0, 0, 0);
      endDate   = new Date(customEndDate).setHours(23, 59, 59, 999);
      if (startDate > endDate) {
        toast.error("Start date cannot be after end date.");
        return;
      }
    } else {
      const range = getDateRange(dateType);
      startDate = range.startDate;
      endDate   = range.endDate;
    }

    const cacheKey = dateType === "custom" ? `${customStartDate}-${customEndDate}` : dateType;
    if (cacheRef.current[cacheKey]) {
      setData(cacheRef.current[cacheKey]);
      return;
    }

    setLoading(true);
    try {
      const res = await adminService.getSuperAdminOpsAUT({ startDate, endDate });
      if (res?.statusCode === 200 && res?.data) {
        cacheRef.current[cacheKey] = res.data;
        setData(res.data);
      } else {
        toast.error(res?.message || "Failed to fetch AUT data.");
      }
    } catch {
      toast.error("An error occurred while fetching AUT data.");
    } finally {
      setLoading(false);
    }
  }, [dateType, customStartDate, customEndDate]);

  const handlePresetChange = (preset: string) => {
    setDateType(preset);
    setSelectedAsset(null);
    setSelectedRealisedAsset(null);
    if (preset !== "custom") {
      const cacheKey = preset;
      if (cacheRef.current[cacheKey]) {
        setData(cacheRef.current[cacheKey]);
        return;
      }
      const range = getDateRange(preset);
      setLoading(true);
      adminService.getSuperAdminOpsAUT(range).then((res) => {
        if (res?.statusCode === 200 && res?.data) {
          cacheRef.current[cacheKey] = res.data;
          setData(res.data);
        } else {
          toast.error(res?.message || "Failed to fetch AUT data.");
        }
      }).catch(() => {
        toast.error("An error occurred while fetching AUT data.");
      }).finally(() => setLoading(false));
    }
  };

  React.useEffect(() => { handlePresetChange("7day"); }, []);

  const realised = data?.realised ?? EMPTY_REALISED;

  const handleExport = () => {
    const wb = XLSX.utils.book_new();
    if (viewMode === "unrealised") {
      if (!filteredTransactions.length) return;
      const unrealisedRows = filteredTransactions.map((t) => ({
        "User Created Date": t.transactionDate,
        "Investor Name":     t.name,
        "AUM User ID":       t.aumUserId,
        "Account Type":      t.accountType,
        "Asset Class":       t.assetClassName,
        "Invested Amount":   t.investedAmount,
        "Current Amount":    t.currentAmount,
        "Gain / Loss":       t.gainLoss,
      }));
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(unrealisedRows), "Unrealised");
    } else {
      if (!filteredRealisedTransactions.length) return;
      const realisedRows = filteredRealisedTransactions.map((t) => ({
        "Last Sell Date":  t.transactionDate,
        "Investor Name":   t.name,
        "AUM User ID":     t.aumUserId,
        "Account Type":    t.accountType,
        "Asset Class":     t.assetClassName,
        "Sell Amount":     t.sellAmount,
        "Sell Count":      t.sellCount,
      }));
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(realisedRows), "Realised");
    }
    XLSX.writeFile(wb, `AUT_Report_${viewMode}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const filteredTransactions = (data?.transactions || []).filter((t) => {
    const matchSearch =
      !search ||
      t.name.toLowerCase().includes(search.toLowerCase()) ||
      t.aumUserId.toLowerCase().includes(search.toLowerCase());
    const matchAsset = !selectedAsset || t.assetClassName === selectedAsset;
    return matchSearch && matchAsset;
  });

  const filteredRealisedTransactions = (realised.transactions || []).filter((t) => {
    const matchSearch =
      !realisedSearch ||
      t.name.toLowerCase().includes(realisedSearch.toLowerCase()) ||
      t.aumUserId.toLowerCase().includes(realisedSearch.toLowerCase());
    const matchAsset = !selectedRealisedAsset || t.assetClassName === selectedRealisedAsset;
    return matchSearch && matchAsset;
  });

  return (
    <Container maxWidth={false} sx={{ py: 4, px: { xs: 2, md: 4 } }}>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          underline="hover"
          color="inherit"
          onClick={() => navigate("/super-admin/ops-dashboard")}
          sx={{ display: "flex", alignItems: "center", gap: 0.5, cursor: "pointer" }}
        >
          <ArrowBack fontSize="small" /> Ops Dashboard
        </Link>
        <Typography color="text.primary" fontWeight={600}>Daily AUT</Typography>
      </Breadcrumbs>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} color={colors.primary}>
            Daily AUT — Assets Under Tracking
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            {viewMode === "unrealised"
              ? "Current holdings still being tracked for users onboarded in the selected date range."
              : "Combined SELL proceeds for users onboarded in the selected date range. Individual mutual funds are not included."}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Download />}
          onClick={handleExport}
          disabled={
            viewMode === "unrealised"
              ? !data?.transactions?.length
              : !realised.transactions.length
          }
          sx={{ borderColor: colors.primary, color: colors.primary }}
        >
          Export Excel
        </Button>
      </Box>

      <ToggleButtonGroup
        exclusive
        value={viewMode}
        onChange={(_event, value) => {
          if (value === "unrealised" || value === "realised") {
            setViewMode(value);
          }
        }}
        sx={{
          mb: 3,
          "& .MuiToggleButton-root": {
            px: 3,
            py: 1,
            textTransform: "none",
            fontWeight: 600,
            borderColor: colors.primary,
            color: colors.primary,
            "&.Mui-selected": {
              backgroundColor: colors.primary,
              color: "#fff",
              "&:hover": { backgroundColor: colors.primary, color: "#fff" },
            },
          },
        }}
      >
        <ToggleButton value="unrealised">Unrealised</ToggleButton>
        <ToggleButton value="realised">Realised</ToggleButton>
      </ToggleButtonGroup>

      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <CalendarToday fontSize="small" sx={{ color: colors.primary }} />
            <Typography variant="subtitle2" fontWeight={600} sx={{ mr: 1 }}>
              Select Range:
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {DATE_PRESETS.map((preset) => (
                <Button
                  key={preset.value}
                  variant={dateType === preset.value ? "contained" : "outlined"}
                  size="small"
                  onClick={() => handlePresetChange(preset.value)}
                  sx={{
                    minWidth: 52,
                    borderColor: colors.primary,
                    backgroundColor: dateType === preset.value ? colors.primary : "transparent",
                    color: dateType === preset.value ? "#fff" : colors.primary,
                    "&:hover": { backgroundColor: colors.primary, color: "#fff" },
                  }}
                >
                  {preset.label}
                </Button>
              ))}
            </Stack>

            {dateType === "custom" && (
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: { xs: 1, sm: 0 } }}>
                <TextField
                  type="date"
                  size="small"
                  label="From"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 160 }}
                />
                <TextField
                  type="date"
                  size="small"
                  label="To"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 160 }}
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => fetchData(true)}
                  sx={{ backgroundColor: colors.primary }}
                >
                  Apply
                </Button>
              </Stack>
            )}
          </Box>
        </CardContent>
      </Card>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress sx={{ color: colors.primary }} />
        </Box>
      ) : data ? (
        <>
          {viewMode === "unrealised" && (
        <>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{ borderRadius: 2, borderLeft: `4px solid ${colors.primary}` }}>
                <CardContent>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>Total Invested</Typography>
                      <Typography variant="h5" fontWeight={700} color={colors.primary}>
                        {formatAmount(data.grandTotal)}
                      </Typography>
                    </Box>
                    <AccountBalanceWallet sx={{ color: colors.primary, fontSize: 36, opacity: 0.7 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{ borderRadius: 2, borderLeft: "4px solid #2563EB" }}>
                <CardContent>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>Current Value</Typography>
                      <Typography variant="h5" fontWeight={700} color="#2563EB">
                        {formatAmount(data.grandTotalCurrent ?? 0)}
                      </Typography>
                    </Box>
                    <TrendingUp sx={{ color: "#2563EB", fontSize: 36, opacity: 0.7 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{ borderRadius: 2, borderLeft: `4px solid ${formatGainLoss(data.grandTotalGainLoss ?? 0).color}` }}>
                <CardContent>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>Gain / Loss</Typography>
                      <Typography variant="h5" fontWeight={700} sx={{ color: formatGainLoss(data.grandTotalGainLoss ?? 0).color }}>
                        {formatGainLoss(data.grandTotalGainLoss ?? 0).text}
                      </Typography>
                    </Box>
                    <TrendingUp sx={{ color: formatGainLoss(data.grandTotalGainLoss ?? 0).color, fontSize: 36, opacity: 0.7 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{ borderRadius: 2, borderLeft: "4px solid #08991F" }}>
                <CardContent>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>Total Transactions</Typography>
                      <Typography variant="h5" fontWeight={700} color="#08991F">
                        {data.totalTransactions.toLocaleString()}
                      </Typography>
                    </Box>
                    <SwapHoriz sx={{ color: "#08991F", fontSize: 36, opacity: 0.7 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{ borderRadius: 2, borderLeft: "4px solid #F17A21" }}>
                <CardContent>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>Unique Investors</Typography>
                      <Typography variant="h5" fontWeight={700} color="#F17A21">
                        {data.totalInvestors.toLocaleString()}
                      </Typography>
                    </Box>
                    <PeopleAlt sx={{ color: "#F17A21", fontSize: 36, opacity: 0.7 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
            Unrealised — Breakdown by Asset Class
            {selectedAsset && (
              <Chip
                label={`Filtered: ${selectedAsset}`}
                size="small"
                onDelete={() => setSelectedAsset(null)}
                sx={{ ml: 2, backgroundColor: colors.primary, color: "#fff" }}
              />
            )}
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {data.assetSummary.map((asset) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={asset.assetType}>
                <Card
                  onClick={() => setSelectedAsset(selectedAsset === asset.assetClassName ? null : asset.assetClassName)}
                  sx={{
                    borderRadius: 2,
                    cursor: "pointer",
                    border: selectedAsset === asset.assetClassName
                      ? `2px solid ${ASSET_CLASS_COLORS[asset.assetClassName] || "#6B7280"}`
                      : "2px solid transparent",
                    transition: "all 0.2s",
                    "&:hover": { boxShadow: 4 },
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                      <Typography
                        variant="subtitle2"
                        fontWeight={600}
                        sx={{ color: ASSET_CLASS_COLORS[asset.assetClassName] || "#6B7280" }}
                      >
                        {asset.assetClassName}
                      </Typography>
                      <TrendingUp sx={{ color: ASSET_CLASS_COLORS[asset.assetClassName] || "#6B7280", fontSize: 20 }} />
                    </Box>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Invested: {formatAmount(asset.totalInvested)}
                    </Typography>
                    <Typography variant="body2" fontWeight={600} color="#2563EB">
                      Current: {formatAmount(asset.totalCurrent ?? 0)}
                    </Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ color: formatGainLoss(asset.totalGainLoss ?? 0).color }}>
                      {formatGainLoss(asset.totalGainLoss ?? 0).text}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {asset.transactionCount} txns
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {asset.uniqueInvestors} investors
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
            {data.assetSummary.length === 0 && (
              <Grid item xs={12}>
                <Typography color="text.secondary" sx={{ py: 2 }}>
                  No asset data found for the selected date range.
                </Typography>
              </Grid>
            )}
          </Grid>

          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 1 }}>
                <Typography variant="h6" fontWeight={600}>
                  Unrealised — Holding Details
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    ({filteredTransactions.length} records)
                  </Typography>
                </Typography>
                <TextField
                  size="small"
                  placeholder="Search by name or AUM ID..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ width: 280 }}
                />
              </Box>

              <TableContainer component={Paper} elevation={0} sx={{ maxHeight: 500, overflow: "auto" }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      {["#", "User Created Date", "Investor Name", "AUM User ID", "Account Type", "Asset Class", "Invested Amount", "Current Amount", "Gain / Loss"].map((h) => (
                        <TableCell
                          key={h}
                          sx={{ fontWeight: 700, backgroundColor: "#f5f7fa", color: colors.primary }}
                        >
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredTransactions.length > 0 ? (
                      filteredTransactions.map((row, idx) => (
                        <TableRow key={idx} hover>
                          <TableCell>{idx + 1}</TableCell>
                          <TableCell>{row.transactionDate}</TableCell>
                          <TableCell sx={{ fontWeight: 500 }}>{row.name}</TableCell>
                          <TableCell>
                            <Chip label={row.aumUserId} size="small" variant="outlined" sx={{ borderColor: colors.primary, color: colors.primary }} />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={row.accountType}
                              size="small"
                              sx={{
                                backgroundColor: row.accountType === "Individual" ? "#EFF6FF" : "#FFF7ED",
                                color: row.accountType === "Individual" ? colors.primary : "#F17A21",
                                fontWeight: 600,
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={row.assetClassName}
                              size="small"
                              sx={{
                                backgroundColor: `${ASSET_CLASS_COLORS[row.assetClassName] || "#6B7280"}22`,
                                color: ASSET_CLASS_COLORS[row.assetClassName] || "#6B7280",
                                fontWeight: 600,
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{formatAmount(row.investedAmount)}</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: "#2563EB" }}>{formatAmount(row.currentAmount ?? 0)}</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: formatGainLoss(row.gainLoss ?? 0).color }}>
                            {formatGainLoss(row.gainLoss ?? 0).text}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={9} align="center" sx={{ py: 4, color: "text.secondary" }}>
                          No transactions found.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </>
          )}

          {viewMode === "realised" && (
        <>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ borderRadius: 2, borderLeft: `4px solid ${colors.primary}` }}>
                <CardContent>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>Total Sell Amount</Typography>
                      <Typography variant="h5" fontWeight={700} color={colors.primary}>
                        {formatAmount(realised.grandTotal)}
                      </Typography>
                    </Box>
                    <AccountBalanceWallet sx={{ color: colors.primary, fontSize: 36, opacity: 0.7 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ borderRadius: 2, borderLeft: "4px solid #08991F" }}>
                <CardContent>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>Sell Transactions</Typography>
                      <Typography variant="h5" fontWeight={700} color="#08991F">
                        {realised.totalTransactions.toLocaleString()}
                      </Typography>
                    </Box>
                    <SwapHoriz sx={{ color: "#08991F", fontSize: 36, opacity: 0.7 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ borderRadius: 2, borderLeft: "4px solid #F17A21" }}>
                <CardContent>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>Unique Investors</Typography>
                      <Typography variant="h5" fontWeight={700} color="#F17A21">
                        {realised.totalInvestors.toLocaleString()}
                      </Typography>
                    </Box>
                    <PeopleAlt sx={{ color: "#F17A21", fontSize: 36, opacity: 0.7 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
            Realised — Breakdown by Asset Class
            {selectedRealisedAsset && (
              <Chip
                label={`Filtered: ${selectedRealisedAsset}`}
                size="small"
                onDelete={() => setSelectedRealisedAsset(null)}
                sx={{ ml: 2, backgroundColor: colors.primary, color: "#fff" }}
              />
            )}
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {realised.assetSummary.map((asset) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={`realised-${asset.assetType}`}>
                <Card
                  onClick={() => setSelectedRealisedAsset(selectedRealisedAsset === asset.assetClassName ? null : asset.assetClassName)}
                  sx={{
                    borderRadius: 2,
                    cursor: "pointer",
                    border: selectedRealisedAsset === asset.assetClassName
                      ? `2px solid ${ASSET_CLASS_COLORS[asset.assetClassName] || "#6B7280"}`
                      : "2px solid transparent",
                    transition: "all 0.2s",
                    "&:hover": { boxShadow: 4 },
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                      <Typography
                        variant="subtitle2"
                        fontWeight={600}
                        sx={{ color: ASSET_CLASS_COLORS[asset.assetClassName] || "#6B7280" }}
                      >
                        {asset.assetClassName}
                      </Typography>
                      <TrendingUp sx={{ color: ASSET_CLASS_COLORS[asset.assetClassName] || "#6B7280", fontSize: 20 }} />
                    </Box>
                    <Typography variant="h6" fontWeight={700} color={colors.primary}>
                      {formatAmount(asset.totalAmount)}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {asset.transactionCount} sells
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {asset.uniqueInvestors} investors
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
            {realised.assetSummary.length === 0 && (
              <Grid item xs={12}>
                <Typography color="text.secondary" sx={{ py: 2 }}>
                  No realised SELL data found for the selected date range.
                </Typography>
              </Grid>
            )}
          </Grid>

          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 1 }}>
                <Typography variant="h6" fontWeight={600}>
                  Realised — Combined Sell Details
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    ({filteredRealisedTransactions.length} records)
                  </Typography>
                </Typography>
                <TextField
                  size="small"
                  placeholder="Search by name or AUM ID..."
                  value={realisedSearch}
                  onChange={(e) => setRealisedSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ width: 280 }}
                />
              </Box>

              <TableContainer component={Paper} elevation={0} sx={{ maxHeight: 500, overflow: "auto" }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      {["#", "Last Sell Date", "Investor Name", "AUM User ID", "Account Type", "Asset Class", "Sell Amount", "Sell Count"].map((h) => (
                        <TableCell
                          key={h}
                          sx={{ fontWeight: 700, backgroundColor: "#f5f7fa", color: colors.primary }}
                        >
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredRealisedTransactions.length > 0 ? (
                      filteredRealisedTransactions.map((row, idx) => (
                        <TableRow key={idx} hover>
                          <TableCell>{idx + 1}</TableCell>
                          <TableCell>{row.transactionDate}</TableCell>
                          <TableCell sx={{ fontWeight: 500 }}>{row.name}</TableCell>
                          <TableCell>
                            <Chip label={row.aumUserId} size="small" variant="outlined" sx={{ borderColor: colors.primary, color: colors.primary }} />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={row.accountType}
                              size="small"
                              sx={{
                                backgroundColor: row.accountType === "Individual" ? "#EFF6FF" : "#FFF7ED",
                                color: row.accountType === "Individual" ? colors.primary : "#F17A21",
                                fontWeight: 600,
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={row.assetClassName}
                              size="small"
                              sx={{
                                backgroundColor: `${ASSET_CLASS_COLORS[row.assetClassName] || "#6B7280"}22`,
                                color: ASSET_CLASS_COLORS[row.assetClassName] || "#6B7280",
                                fontWeight: 600,
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{formatAmount(row.sellAmount)}</TableCell>
                          <TableCell>{row.sellCount}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 4, color: "text.secondary" }}>
                          No realised sells found.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </>
          )}
        </>
      ) : (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <Typography color="text.secondary">Select a date range to load AUT data.</Typography>
        </Box>
      )}
    </Container>
  );
};

export default DailyAUT;
